import { useCallback, useRef, useState, type PointerEvent, type RefObject } from 'react';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import { clampChartHeight, nearestChartSize } from '../DashboardPresetGrid/chartLayout';

/** Граница карточки, за которую тянут: правая задаёт ширину, нижняя — высоту. */
export type ChartResizeEdge = 'width' | 'height';

/** Начало растягивания: от этих величин отсчитывается новый размер диаграммы. */
type ResizeStart = {
  edge: ChartResizeEdge;
  pointerX: number;
  pointerY: number;
  cardWidth: number;
  cardHeight: number;
  gridWidth: number;
};

type Params = {
  /** Карточка диаграммы — от её размеров отсчитывается растягивание */
  cardRef: RefObject<HTMLDivElement | null>;
  /** Ширина сетки дашборда: по ней доля ширины диаграммы считается заново */
  getGridWidth: () => number;
  onResize: (change: ChartSizeChange) => void;
};

/**
 * Растягивание границ диаграммы на сетке.
 *
 * Ширина сразу встаёт на ближайшую долю каталога, а не на любое число пикселей: доли делят
 * сетку нацело, и промежуточная ширина оставила бы в ряду дыру. Размер считается от размеров
 * карточки на начало растягивания, поэтому диаграмма не «убегает» от курсора, перестраиваясь
 * под каждую новую долю.
 */
export const useChartCardResize = ({ cardRef, getGridWidth, onResize }: Params) => {
  const startRef = useRef<ResizeStart | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const handlePointerDown = useCallback(
    (edge: ChartResizeEdge, event: PointerEvent<HTMLDivElement>) => {
      const card = cardRef.current;
      if (!card) return;
      const { width, height } = card.getBoundingClientRect();
      startRef.current = {
        edge,
        pointerX: event.clientX,
        pointerY: event.clientY,
        cardWidth: width,
        cardHeight: height,
        gridWidth: getGridWidth(),
      };
      // Захват указателя: растягивание продолжается, даже когда курсор ушёл с узкой границы.
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
      setIsResizing(true);
    },
    [cardRef, getGridWidth],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const start = startRef.current;
      if (!start) return;
      if (start.edge === 'width') {
        onResize({ size: nearestChartSize(start.cardWidth + event.clientX - start.pointerX, start.gridWidth) });
        return;
      }
      onResize({ height: clampChartHeight(start.cardHeight + event.clientY - start.pointerY) });
    },
    [onResize],
  );

  const handlePointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!startRef.current) return;
    startRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsResizing(false);
  }, []);

  return { isResizing, handlePointerDown, handlePointerMove, handlePointerUp };
};
